import * as api from "@/api";
import LocalWallpaper from "@/classes/local-wallpaper";
import {
	changeEditorFontSize,
	codeChanged,
	editorInstance,
	enableMonacoEditor,
	reloadMonacoEditor,
	setValue,
	wrap,
} from "@/code-editor";
import App from "@/components/app";
import { loadDependencies } from "@/dependencies";
import { handleDrop } from "@/drop";
import {
	deleteFile,
	handleSaveClick,
	isFileOpened,
	renameFilePrompt,
} from "@/file";
import globals from "@/globals";
import "@/globals.css";
import { initI18n } from "@/i18n";
import * as signals from "@/signals";
import { LoginInfo } from "@/types";
import { encodeData, finishLoading, loggedIn, showDialog } from "@/utils";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import i18next, { t } from "i18next";
import ReactDOM from "react-dom/client";

const queryClient = new QueryClient();

window.addEventListener("dragover", (event) => {
	event.preventDefault();
});

window.addEventListener("drop", (event) => {
	event.preventDefault();
	void handleDrop(event);
});

window.addEventListener("keydown", (event) => {
	const key = event.key.toLowerCase();
	if (event.ctrlKey || event.metaKey) {
		const dispatchToCodeEditor = (): boolean => {
			if (
				editorInstance &&
				document.activeElement &&
				document.activeElement.nodeName !== "TEXTAREA" &&
				!document.getElementsByClassName("popup").length
			) {
				editorInstance.focus();
				setTimeout(() => {
					document
						.getElementsByClassName("monaco-editor")[0]
						.dispatchEvent(event);
				}, 1);
				return true;
			}
			return false;
		};

		switch (key) {
			case "backspace": {
				event.preventDefault();
				if (signals.currentFile.value) {
					void deleteFile(signals.currentFile.value);
				}
				break;
			}
			case "0": {
				globals.settings.fontSize = 14;
				changeEditorFontSize(true);
				break;
			}
			case "a":
			case "f": {
				if (dispatchToCodeEditor()) {
					event.preventDefault();
				}
				break;
			}
			case "s": {
				event.preventDefault();
				if (
					!signals.currentFile.value ||
					typeof signals.files.value[signals.currentFile.value] ===
						"string"
				) {
					handleSaveClick();
				}
				break;
			}
			case "=": {
				event.preventDefault();
				if (globals.settings.fontSize < 72) {
					globals.settings.fontSize = globals.settings.fontSize + 2;
					changeEditorFontSize(true);
				}
				break;
			}
			case "-": {
				event.preventDefault();
				if (globals.settings.fontSize > 12) {
					globals.settings.fontSize = globals.settings.fontSize - 2;
					changeEditorFontSize(true);
				}
				break;
			}
			default:
				break;
		}
	} else if (event.altKey) {
		if (event.code === "KeyZ") {
			// event.key would be ω
			event.preventDefault();
			wrap();
		}
	} else if (key === "f2") {
		if (isFileOpened()) {
			void renameFilePrompt(signals.currentFile.value);
		}
	}
});

window.addEventListener("message", (event) => {
	try {
		if (typeof event.data !== "string") {
			return;
		}
		const data = JSON.parse(
			decodeURIComponent(window.atob(event.data)),
		) as {
			action?: string;
			birthday?: string;
			email?: string;
			name?: string;
			text?: string;
			token?: string;
			username?: string;
		};
		switch (data.action) {
			case "alert": {
				if (data.text) {
					void showDialog(data.text);
				}
				break;
			}
			case "login": {
				delete data.action;
				signals.login.value = data;
				loggedIn(true);
				break;
			}
			default:
				break;
		}
	} catch (error) {
		if (process.env.NODE_ENV === "development") {
			console.warn(event.data, error);
		}
	}
});

window.addEventListener("DOMContentLoaded", () => {
	if (
		!globals.isMonacoSupported ||
		globals.settings.codeEditor === "native" ||
		(!globals.settings.codeEditor && (globals.isMobile || globals.isSafari))
	) {
		const codeTextArea = document.getElementById(
			"code",
		) as HTMLTextAreaElement;
		codeTextArea.hidden = false;
		codeTextArea.setAttribute("aria-label", t("codeEditor"));
		codeTextArea.style.fontSize = globals.settings.fontSize + "px";
		codeTextArea.addEventListener("input", codeChanged);

		globals.loadCount++;
		finishLoading();
	} else {
		enableMonacoEditor(1000)
			.then(() => {
				globals.loadCount++;
				finishLoading();

				let reloadCount = 0;
				const intervalId = setInterval(() => {
					if (signals.currentSite.value || reloadCount >= 2) {
						clearInterval(intervalId);
						return;
					}
					reloadMonacoEditor();
					reloadCount++;
				}, 2000);
			})
			.catch(console.error);
	}
});

window.addEventListener("load", loadDependencies);

void (async (): Promise<void> => {
	if (
		process.env.NODE_ENV === "production" &&
		!window.location.hostname?.endsWith(".retiehe.com")
	) {
		window.location.href = "https://host.retiehe.com/";
		return;
	}

	await initI18n();
	document.documentElement.lang = i18next.language;
	document.title = t("rthWebHosting");

	if (!navigator.cookieEnabled) {
		window.alert(t("cookieDisabled"));
	}

	ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
		<QueryClientProvider client={queryClient}>
			<App />
		</QueryClientProvider>,
	);

	try {
		const data = await api.getInit();
		signals.dynamicInfo.value = {
			...data,
			isLoaded: true,
		};
		if (data.login) {
			try {
				signals.login.value =
					typeof data.login === "string"
						? (JSON.parse(data.login) as LoginInfo)
						: data.login;
				loggedIn(false);
			} catch (error) {
				console.error(error);
			}
		} else {
			signals.isLoginPanelShown.value = true;
			try {
				const data = await api.getIntro();
				if (data && !signals.currentSite.value) {
					setValue(data);
				}
			} catch (error) {
				void api.handleApiError(error);
			} finally {
				finishLoading();
			}
		}
		if (data.blockReason) {
			window.location.href =
				"https://account.retiehe.com/blocked?" +
				encodeData({ reason: data.blockReason });
			return;
		}
		if (data.redirectTo) {
			window.location.href = data.redirectTo;
			return;
		}
		if (data.alert) {
			void showDialog(data.alert);
		}
	} catch (error) {
		console.error(error);
		signals.isSplashScreenShown.value = false;
		void showDialog(t("essentialComponentNotLoaded"));
	}

	globals.themeColor.name = "theme-color";
	globals.themeColor.content = globals.isLight ? "#ffffff" : "#000000";
	document.head.appendChild(globals.themeColor);

	if (globals.isLight) {
		document.documentElement.classList.add("light");
	}
	if (globals.settings.theme === "wallpaper") {
		const localWallpaper = LocalWallpaper.getInstance();
		void localWallpaper.loadWallpaper();
	}

	if (!/bot|spider/i.test(navigator.userAgent)) {
		try {
			const data = await api.checkBrowser();
			globals.browserCheck.grade = data.grade;
			globals.browserCheck.isBadDns = data.isBadDns;
			if (data.isBrowser && data.grade <= 30) {
				await showDialog(data.description, {
					isHtml: true,
					showCancel: true,
				});
				window.open("https://check.retiehe.com/");
			}
		} catch (error) {
			void api.handleApiError(error);
		}
	}
})();
